import React, { useState, useEffect } from 'react'
import { Zap, Thermometer, Wind, Wrench, Droplets, PaintBucket } from 'lucide-react'
import { servicesApi } from '../../services/api'

// 图标映射
const iconMap: Record<string, React.ReactNode> = {
  'Zap': <Zap className="w-8 h-8" />,
  'Thermometer': <Thermometer className="w-8 h-8" />,
  'Wind': <Wind className="w-8 h-8" />,
  'Wrench': <Wrench className="w-8 h-8" />,
  'Droplets': <Droplets className="w-8 h-8" />,
  'PaintBucket': <PaintBucket className="w-8 h-8" />,
}

// 默认图标
const defaultIcons: string[] = ['Zap', 'Thermometer', 'Wind', 'Wrench', 'Droplets', 'PaintBucket']

// 默认服务详情（硬编码，API返回的description是简介）
const defaultDetails: Record<number, { details_zh: string[]; details_vi: string[] }> = {
  1: {
    details_zh: [
      '工业配电系统设计与安装',
      'PLC控制系统编程与调试',
      '工业照明系统优化',
      '电气安全检测与维护',
      '节能改造方案实施'
    ],
    details_vi: [
      'Thiết kế và lắp đặt hệ thống phân phối điện công nghiệp',
      'Lập trình và vận hành hệ thống điều khiển PLC',
      'Tối ưu hóa hệ thống chiếu sáng công nghiệp',
      'Kiểm tra an toàn điện và bảo trì',
      'Thực hiện giải pháp cải tạo tiết kiệm năng lượng'
    ]
  },
  2: {
    details_zh: [
      '中央空调系统设计与安装',
      '工业通风系统优化',
      '温度控制系统集成',
      '能效评估与改进',
      '定期维护与保养'
    ],
    details_vi: [
      'Thiết kế và lắp đặt hệ thống điều hòa trung tâm',
      'Tối ưu hóa hệ thống thông gió công nghiệp',
      'Tích hợp hệ thống điều khiển nhiệt độ',
      'Đánh giá và cải thiện hiệu suất năng lượng',
      'Bảo trì và bảo dưỡng định kỳ'
    ]
  },
  3: {
    details_zh: [
      '工业排风系统设计',
      '新风系统安装',
      '废气处理系统',
      '风管设计与施工',
      '通风系统测试与调试'
    ],
    details_vi: [
      'Thiết kế hệ thống xả thải công nghiệp',
      'Lắp đặt hệ thống gió tươi',
      'Hệ thống xử lý khí thải',
      'Thiết kế và thi công ống gió',
      'Kiểm tra và vận hành hệ thống thông gió'
    ]
  },
  4: {
    details_zh: [
      '厂房内部装修',
      '办公室设计与施工',
      '生产线布局优化',
      '钢结构加固与改造',
      '建筑维护与修缮'
    ],
    details_vi: [
      'Trang trí nội thất nhà xưởng',
      'Thiết kế và thi công văn phòng',
      'Tối ưu hóa bố trí dây chuyền sản xuất',
      'Gia cố và cải tạo kết cấu thép',
      'Bảo trì và sửa chữa công trình'
    ]
  },
  5: {
    details_zh: [
      '环氧地坪施工',
      '聚氨酯地坪涂装',
      '地面防水处理',
      '防静电地坪',
      '耐磨地坪维护'
    ],
    details_vi: [
      'Thi công sàn epoxy',
      'Sơn sàn polyurethane',
      'Xử lý chống thấm sàn',
      'Sàn chống tĩnh điện',
      'Bảo trì sàn chịu mài mòn'
    ]
  },
  6: {
    details_zh: [
      '项目规划与咨询',
      '机电一体化设计',
      '施工管理服务',
      '质量检测与验收',
      '后期维护支持'
    ],
    details_vi: [
      'Lập kế hoạch và tư vấn dự án',
      'Thiết kế tích hợp cơ điện',
      'Dịch vụ quản lý thi công',
      'Kiểm tra chất lượng và nghiệm thu',
      'Hỗ trợ bảo trì sau này'
    ]
  }
}

// 默认图片（API返回空时使用）
const defaultImages: string[] = [
  'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1619894991204-f092c50c2e0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1610141160423-54c5b4608a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1503387769-00a112127ca0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
]

// 根据 icon_class 名称返回对应图标
function getIcon(iconClass?: string): React.ReactNode {
  if (!iconClass) return <Zap className="w-8 h-8" />
  // iconClass 可能是 "fa fa-zap" 或 "Zap" 格式
  const name = iconClass.replace(/^fa fa-/, '')
  // 首字母大写
  const key = name.charAt(0).toUpperCase() + name.slice(1)
  return iconMap[key] || <Zap className="w-8 h-8" />
}

interface ServiceData {
  id: number | string
  icon: React.ReactNode
  title_zh: string
  title_vi: string
  description_zh: string
  description_vi: string
  imageUrl: string
  details_zh: string[]
  details_vi: string[]
}

const CoreServices = () => {
  const [isVietnamese, setIsVietnamese] = useState(false)
  const [selectedService, setSelectedService] = useState<number>(1)
  const [services, setServices] = useState<ServiceData[]>([])

  // 从 API 加载服务数据
  useEffect(() => {
    servicesApi.getAll()
      .then(res => {
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          const mapped = res.data.map((item: any, index: number) => {
            const sortOrder = item.sort_order || index + 1
            const id = item.id || sortOrder

            // 解析 details 字段（后端存储为 JSON 字符串）
            let details_zh: string[], details_vi: string[]
            try {
              details_zh = typeof item.details_zh === 'string' ? JSON.parse(item.details_zh) : (item.details_zh || [])
            } catch {
              details_zh = defaultDetails[id]?.details_zh || defaultDetails[1].details_zh
            }
            try {
              details_vi = typeof item.details_vi === 'string' ? JSON.parse(item.details_vi) : (item.details_vi || [])
            } catch {
              details_vi = defaultDetails[id]?.details_vi || defaultDetails[1].details_vi
            }

            // 如果 API 返回的详情为空，用默认值填充
            if (details_zh.length === 0) {
              details_zh = defaultDetails[id]?.details_zh || defaultDetails[1].details_zh
            }
            if (details_vi.length === 0) {
              details_vi = defaultDetails[id]?.details_vi || defaultDetails[1].details_vi
            }

            return {
              id: id,
              icon: getIcon(item.icon_class),
              title_zh: item.name_zh || '',
              title_vi: item.name_vi || '',
              description_zh: item.description_zh || '',
              description_vi: item.description_vi || '',
              imageUrl: item.image_url || defaultImages[index % defaultImages.length],
              details_zh: details_zh,
              details_vi: details_vi,
            }
          })
          setServices(mapped)
        } else {
          // API 返回空数据，用默认数据
          setServices(getDefaultServices())
        }
      })
      .catch(() => {
        // API 请求失败，用默认数据
        setServices(getDefaultServices())
      })
  }, [])

  // 默认服务数据（API 不可用时的后备）
  function getDefaultServices(): ServiceData[] {
    return [
      { id: 1, icon: <Zap className="w-8 h-8" />, title_zh: '电气系统工程', title_vi: 'Hệ thống kỹ thuật điện', description_zh: '工业电气系统设计、安装、调试与维护，包括配电系统、照明系统、控制系统等。', description_vi: 'Thiết kế, lắp đặt, vận hành và bảo trì hệ thống điện công nghiệp, bao gồm hệ thống phân phối điện, hệ thống chiếu sáng, hệ thống điều khiển, v.v.', imageUrl: defaultImages[0], ...defaultDetails[1] },
      { id: 2, icon: <Thermometer className="w-8 h-8" />, title_zh: '暖通与空调系统工程', title_vi: 'Hệ thống HVAC', description_zh: '工业厂房暖通空调系统设计、安装与维护，确保舒适的工作环境和设备稳定运行。', description_vi: 'Thiết kế, lắp đặt và bảo trì hệ thống HVAC cho nhà xưởng công nghiệp, đảm bảo môi trường làm việc thoải mái và vận hành thiết bị ổn định.', imageUrl: defaultImages[1], ...defaultDetails[2] },
      { id: 3, icon: <Wind className="w-8 h-8" />, title_zh: '通风系统工程', title_vi: 'Hệ thống thông gió', description_zh: '工业厂房通风系统设计与施工，确保空气流通和工作场所环境安全。', description_vi: 'Thiết kế và thi công hệ thống thông gió cho nhà xưởng công nghiệp, đảm bảo lưu thông không khí và an toàn môi trường làm việc.', imageUrl: defaultImages[2], ...defaultDetails[3] },
      { id: 4, icon: <Wrench className="w-8 h-8" />, title_zh: '建筑装饰及改造工程', title_vi: 'Trang trí và cải tạo công trình', description_zh: '厂房装修、改造与维护，提升工作环境质量和建筑功能性。', description_vi: 'Trang trí, cải tạo và bảo trì nhà xưởng, nâng cao chất lượng môi trường làm việc và chức năng xây dựng.', imageUrl: defaultImages[3], ...defaultDetails[4] },
      { id: 5, icon: <Droplets className="w-8 h-8" />, title_zh: '地面防水及地坪漆工程', title_vi: 'Chống thấm sàn và sơn sàn', description_zh: '工业地坪防水处理与涂料施工，提升地面耐久性和安全性。', description_vi: 'Xử lý chống thấm và thi công sơn sàn công nghiệp, nâng cao độ bền và an toàn của sàn.', imageUrl: defaultImages[4], ...defaultDetails[5] },
      { id: 6, icon: <PaintBucket className="w-8 h-8" />, title_zh: '综合工程服务', title_vi: 'Dịch vụ kỹ thuật tổng hợp', description_zh: '机电一体化解决方案，提供从设计到施工的全流程服务。', description_vi: 'Giải pháp tích hợp cơ điện, cung cấp dịch vụ toàn trình từ thiết kế đến thi công.', imageUrl: defaultImages[5], ...defaultDetails[6] },
    ]
  }

  const selectedServiceData = services.find(s => Number(s.id) === selectedService) || services[0]
  const serviceList = services.length > 0 ? services : getDefaultServices()

  return (
    <section className="section-padding">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 animate-fade-in">
              {isVietnamese ? 'Dịch vụ chính' : '核心业务'}
            </h2>
            <div className="h-1 w-24 bg-blue-600 rounded-full mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl">
              {isVietnamese 
                ? 'Chuyên cung cấp các dịch vụ kỹ thuật chuyên nghiệp cho nhà máy công nghiệp và doanh nghiệp tại Bắc Ninh Việt Nam'
                : '专业为越南北宁的工业厂房和企业提供专业技术服务'}
            </p>
          </div>
          <button
            onClick={() => setIsVietnamese(!isVietnamese)}
            className="btn-secondary text-sm px-4 py-2"
          >
            {isVietnamese ? '中文 / Tiếng Trung' : 'Tiếng Việt / 越南语'}
          </button>
        </div>

        {/* 服务列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {serviceList.map((service) => (
            <button
              key={service.id}
              onClick={() => setSelectedService(Number(service.id))}
              className={`p-6 rounded-xl text-left transition-all duration-300 ${
                Number(selectedService) === Number(service.id)
                  ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                  : 'bg-white text-gray-800 hover:bg-blue-50 hover:shadow-md'
              }`}
            >
              <div className="flex items-start mb-4">
                <div className={`p-3 rounded-lg mr-4 ${
                  Number(selectedService) === Number(service.id) ? 'bg-white bg-opacity-20' : 'bg-blue-100'
                }`}>
                  <div className={Number(selectedService) === Number(service.id) ? 'text-white' : 'text-blue-600'}>
                    {service.icon}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    {isVietnamese ? service.title_vi : service.title_zh}
                  </h3>
                  <p className={`text-sm ${Number(selectedService) === Number(service.id) ? 'text-blue-100' : 'text-gray-600'}`}>
                    {isVietnamese ? service.description_vi : service.description_zh}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* 服务详情 */}
        {selectedServiceData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 bg-gray-50 p-4 md:p-8 rounded-2xl animate-scale-in">
            <div>
              <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
                {isVietnamese ? selectedServiceData.title_vi : selectedServiceData.title_zh}
              </h3>
              <p className="text-sm md:text-lg text-gray-700 mb-6 md:mb-8">
                {isVietnamese ? selectedServiceData.description_vi : selectedServiceData.description_zh}
              </p>
              
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <h4 className="text-base md:text-xl font-semibold text-blue-800">
                  {isVietnamese ? 'Chi tiết dịch vụ' : '服务详情'}
                </h4>
                <ul className="space-y-2 md:space-y-3">
                  {(isVietnamese ? selectedServiceData.details_vi : selectedServiceData.details_zh).map((detail, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-600 rounded-full mt-1.5 md:mt-2 mr-2 md:mr-3 flex-shrink-0"></div>
                      <span className="text-sm md:text-base text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 p-4 md:p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">
                  {isVietnamese ? 'Ưu điểm của chúng tôi' : '我们的优势'}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-700">
                      {isVietnamese ? 'Đội ngũ địa phương' : '本地施工团队'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-700">
                      {isVietnamese ? 'Kinh nghiệm phong phú' : '丰富经验'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-700">
                      {isVietnamese ? 'Thi công linh hoạt' : '灵活施工'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-700">
                      {isVietnamese ? 'Chất lượng đảm bảo' : '质量保证'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={selectedServiceData.imageUrl} 
                alt={isVietnamese ? selectedServiceData.title_vi : selectedServiceData.title_zh}
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 rounded-b-xl">
                <div className="text-white">
                  <p className="text-sm opacity-90">
                    {isVietnamese 
                      ? 'Hình ảnh dự án tiêu biểu'
                      : '典型项目图片'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default CoreServices
